import { ginzaxiaomaApiRequest } from './index';

type UploadSingleParams = {
  file: File;
  type: 'bag' | 'detail' | 'accessory';
  bagMeta?: Record<string, unknown>;
  bagId?: string;
  authenticationBagId?: number;
};

type UploadSingleResponse = {
  code: number;
  message: string;
  data: {
    originalName: string;
    backendName: string;
    backendPath: string;
    type: string;
    uploadTime: string;
    bagMeta?: Record<string, unknown>;
    bagId: number;
    savedImageId: number;
    authenticationBagId: number;
  };
};

type SaveBagInfoWithImagesParams = {
  bagInfo: {
    id?: number;
    bagId: string;
    omsRecycleOrderId?: string;
    bagMeta?: Record<string, unknown>;
  };
  images: Array<{
    backendPath: string;
    type: string;
    originalName: string;
    publicUrl: string;
    batchId?: string;
  }>;
};

type SaveBagInfoWithImagesResponse = {
  bagInfo: {
    id: number;
    bagId: string;
    bagMeta?: Record<string, unknown>;
    createdAt: string;
  };
  images: Array<{
    id: number;
    authenticationBagId: number;
    backendPath: string;
    type: string;
    originalName: string;
    publicUrl: string;
  }>;
};

type SubmitAuthenticationParams = {
  authenticationBagId: number;
  imagePaths: string[];
  bagInfo: {
    brand?: string;
    names?: string;
    name?: string;
    color?: string;
    rank?: string;
    material?: string;
    hardware?: string;
    stamp?: string;
    accessory?: string;
    size?: string;
  };
  bagId: string;
};

type AuthenticationTaskStatus =
  | 'processing'
  | 'completed'
  | 'failed'
  | 'queued';

type SubmitAuthenticationResponse = {
  code: number;
  message: string;
  data: {
    authenticationId: string;
    status: AuthenticationTaskStatus;
    submitTime: string;
    estimatedCompletionTime: string;
    queuePosition: number;
    authenticationTaskId: number;
    certificateNo?: string;
  };
};

type SyncImagesToDatalakeParams = {
  omsRecycleOrderId: string;
};

type SyncImagesToDatalakeResponse = {
  status: 'queued';
  jobId: string;
};

type GetTasksParams = {
  pageNum: number;
  pageSize: number;
  certificateNo?: string;
  reviewStatus?: Array<'pending' | 'genuine' | 'fake'>;
};

type UpdateReviewStatusParams = {
  id: number;
  reviewStatus: 'pending' | 'genuine' | 'fake';
};

type UpdateDetectionPointReviewParams = {
  id: number;
  reviewComment: string;
};

type GetTasksResponse = {
  list: Array<{
    id: number;
    authenticationBagId: number;
    authenticationId: string;
    certificateNo?: string;
    bagId: string;
    bagInfo: Record<string, unknown>;
    imagePaths: string[];
    submitTime: string;
    queuePosition: number;
    estimatedCompletionTime: string;
    createdAt: string;
    status: AuthenticationTaskStatus;
    data: Record<string, unknown>;
    result: {
      id: number;
      authenticationTaskId: number;
      authenticationId: string;
      status: AuthenticationTaskStatus;
      completionTime?: string;
      authenticityScore?: number;
      authenticityLevel?: string;
      confidence?: number;
      verdict?: string;
      createdAt: string;
      updatedAt: string;
      reviewStatus?: 'pending' | 'genuine' | 'fake';
      reviewedBy?: string | null;
      reviewedAt?: string | null;
      detectionPoints: Array<{
        id: number;
        authenticationResultId: number;
        pointId: string;
        labelId: number;
        category: string;
        coordinates: { x: number; y: number; width: number; height: number };
        score: number;
        similarity: number;
        status: string;
        description: string | null;
        referenceImages: string[];
        createdAt: string;
        originalName: string | null;
        reviewedBy?: string | null;
        reviewedAt?: string | null;
        reviewComment?: string | null;
      }>;
    } | null;
  }>;
  total: number;
  pageNum: number;
  pageSize: number;
};

const authenticationApi = {
  uploadSingle({
    file,
    type,
    bagMeta,
    bagId,
    authenticationBagId,
  }: UploadSingleParams) {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);
    if (bagMeta) {
      formData.append('bagMeta', JSON.stringify(bagMeta));
    }
    if (bagId) {
      formData.append('bagId', bagId);
    }
    if (authenticationBagId) {
      formData.append('authenticationBagId', String(authenticationBagId));
    }

    return ginzaxiaomaApiRequest.post<UploadSingleResponse>(
      '/admin/authentication-bag/upload-single',
      formData,
    );
  },

  saveBagInfoWithImages(params: SaveBagInfoWithImagesParams) {
    return ginzaxiaomaApiRequest.post<SaveBagInfoWithImagesResponse>(
      '/admin/authentication-bag/save-bag-info-with-images',
      params,
    );
  },

  submitAuthentication(params: SubmitAuthenticationParams) {
    return ginzaxiaomaApiRequest.post<SubmitAuthenticationResponse>(
      '/admin/authentication-bag/submit-authentication',
      params,
    );
  },

  getAuthenticationDetail(authenticationBagId: string) {
    return ginzaxiaomaApiRequest.get<{
      bagInfo: {
        id: number;
        bagId: string;
        bagMeta: {
          brand?: string;
          name?: string;
          names?: string;
          color?: string;
          rank?: string;
          material?: string;
          hardware?: string;
          stamp?: string;
          accessory?: string;
          size?: string;
        };
        createdAt: string;
        deletedAt: string | null;
      };
      images: Array<{
        id: number;
        authenticationBagId: number;
        bagId: string;
        backendPath: string;
        type: string;
        originalName: string;
        batchId: string | null;
        createdAt: string;
        deletedAt: string | null;
        publicUrl: string;
      }>;
      latestTask: {
        id: number;
        authenticationBagId: number;
        authenticationId: string;
        bagId: string;
        bagInfo: Record<string, unknown>;
        imagePaths: string[];
        submitTime: string;
        queuePosition: number;
        estimatedCompletionTime: string;
        createdAt: string;
        status: AuthenticationTaskStatus;
        certificateNo?: string;
      } | null;
      latestResult: {
        id: number;
        authenticationTaskId: number;
        status: AuthenticationTaskStatus;
        completionTime: string;
        authenticityScore: number;
        authenticityLevel: string;
        confidence: number;
        verdict: string;
        reviewStatus?: 'pending' | 'genuine' | 'fake' | null;
        createdAt: string;
        updatedAt: string;
        authenticationId: string;
      } | null;
      detectionPoints: Array<{
        id: number;
        authenticationResultId: number;
        pointId: string;
        labelId: number;
        category: string;
        coordinates: { x: number; y: number; width: number; height: number };
        score: number;
        similarity: number;
        status: string;
        description: string | null;
        referenceImages: string[];
        createdAt: string;
        originalName: string | null;
        publicUrl: string | null;
        reviewedBy: string | null;
        reviewedAt: string | null;
        reviewComment: string | null;
      }>;
    }>(`/admin/authentication-bag/${authenticationBagId}/detail`);
  },

  deleteImage(imageId: number) {
    return ginzaxiaomaApiRequest.delete(
      `/admin/authentication-bag/image/${imageId}`,
    );
  },

  getAuthenticationTasks(params: GetTasksParams) {
    return ginzaxiaomaApiRequest.get<GetTasksResponse>(
      '/admin/authentication-bag/tasks',
      { params },
    );
  },

  updateReviewStatus(params: UpdateReviewStatusParams): Promise<void> {
    return ginzaxiaomaApiRequest.post<void>(
      '/admin/authentication-bag/result/review-status',
      params,
    ) as unknown as Promise<void>;
  },

  updateDetectionPointReview(
    params: UpdateDetectionPointReviewParams,
  ): Promise<void> {
    return ginzaxiaomaApiRequest.post<void>(
      '/admin/authentication-bag/detection-point/review',
      params,
    ) as unknown as Promise<void>;
  },

  syncImagesToDatalake(params: SyncImagesToDatalakeParams) {
    return ginzaxiaomaApiRequest.post<SyncImagesToDatalakeResponse>(
      '/admin/authentication-bag/sync-images-to-datalake',
      params,
    );
  },

  imageProxy(path: string): Promise<Blob> {
    const normalizedPath = path.startsWith('http://')
      ? path.replace('http://', 'https://')
      : path;
    return ginzaxiaomaApiRequest.get('/admin/authentication-bag/image-proxy', {
      params: { path: normalizedPath },
      responseType: 'blob',
    }) as unknown as Promise<Blob>;
  },
};

export default authenticationApi;
